/**
 * Classic page ↔ New page, always visible at the top right of a job (#1814).
 *
 * The "Try the new job page" switch used to live only at the bottom of the
 * classic page's ⋯ menu, right under "+ Task" — and the way back only in the
 * new page's "More for this job" / ⌘K list. Darcy, aiming for Task on job
 * #092678289, landed on the new page with no idea what happened or how to get
 * back, and Corbin couldn't find the way back either. So the switch is now a
 * two-button toggle in plain sight: this one on the CLASSIC page's header, the
 * new page's "Job at a glance" header carries its own (#1825). The classic ⋯
 * menu no longer carries it (a one-slip mis-click there flipped her page).
 *
 * Shown only to people the switch is offered to (office_review.manage) —
 * nobody else can ever see the new page. The preference itself is unchanged:
 * per user, server-backed, follows them across machines (lib/finish-line.ts).
 */
import { LayoutTemplate, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function JobPageVersionToggle({
    isNew,
    onChange,
    className,
}: {
    /** The person's switch is on (the new page is showing, or loading). */
    isNew: boolean;
    onChange: (wantNew: boolean) => void;
    className?: string;
}) {
    const pick = (wantNew: boolean) => {
        if (wantNew === isNew) return;
        onChange(wantNew);
        toast.success(
            wantNew
                ? "New job page on. Only your screen changed. Switch back any time, top right."
                : "Classic job page back on. Only your screen changed.",
        );
    };
    const base =
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    return (
        <div
            role="radiogroup"
            aria-label="Which job page you see"
            data-testid="job-page-version-toggle"
            title="Which job page you see. Only changes your own screen."
            className={cn("inline-flex shrink-0 items-center gap-0.5 rounded-full border bg-card p-0.5", className)}
        >
            <button
                type="button"
                role="radio"
                aria-checked={!isNew}
                data-testid="job-page-version-classic"
                onClick={() => pick(false)}
                className={cn(
                    base,
                    !isNew
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
            >
                <LayoutTemplate className="h-3.5 w-3.5" /> Classic page
            </button>
            <button
                type="button"
                role="radio"
                aria-checked={isNew}
                data-testid="job-page-version-new"
                onClick={() => pick(true)}
                className={cn(
                    base,
                    isNew
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
            >
                <Sparkles className="h-3.5 w-3.5" /> New page
            </button>
        </div>
    );
}
